import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { RiArrowLeftSLine } from 'react-icons/ri';

// Spinner só ganha visibilidade se o carregamento durar mais que isto: nas remontagens
// quentes (caches de sessão) a guarda resolve em poucos frames e mostrar "Carregando…"
// por <100ms vira um pisca que parece defeito. Abaixo do limiar, o usuário vê apenas o
// shell estável da página.
const SPINNER_DELAY_MS = 250;

// Continuidade entre instâncias: no caminho FRIO o gate mostra o spinner e o fluxo
// remonta este componente — sem isto o timer zeraria e o spinner piscaria para fora e
// para dentro. Se um spinner esteve visível há menos que esta janela, a instância nova
// já nasce com ele.
const SPINNER_CONTINUITY_MS = 1000;

let lastSpinnerVisibleAt = 0;

// Shell comum das telas de transição da apreciação (voltar + título) — usado pelo
// loading e pelos estados de erro/sem-acesso do gate, para manter a página "montada"
// visualmente enquanto os dados resolvem.
export function FlowShellHeader({ backTo = '/minha-equipe' }: { backTo?: string } = {}) {
  return (
    <div className="flex flex-row items-center space-x-3 mb-16">
      <div>
        <Link to={backTo} tabIndex={-1}>
          <button className="rounded-full bg-purple flex justify-center items-center p-1" aria-label="Voltar">
            <RiArrowLeftSLine size={28} className="text-white" />
          </button>
        </Link>
      </div>
      <h1 tabIndex={0} className="text-lg lg:text-3xl py-1 rounded-full">
        Minha Equipe
      </h1>
    </div>
  );
}

// "Sem acesso" das telas de equipe: usado pelo gate da apreciação (elegibilidade resolvida sem
// fluxo) e por /minha-equipe/ para quem não é gestor, que antes via a página em branco
// (HOM-06, 2026-10-22). `backTo` troca o destino do "Voltar" quando a própria /minha-equipe é
// a tela negada.
export function FlowNoAccess({ backTo }: { backTo?: string } = {}) {
  return (
    <div className="mx-auto container p-4 lg:p-10">
      <FlowShellHeader backTo={backTo} />
      <div className="border-2 border-electric-blue rounded-3xl p-8 bg-oxford-blue">
        <p tabIndex={0} className="text-dandelion text-lg font-semibold block text-center">
          Você não possui acesso à esta página.
        </p>
      </div>
    </div>
  );
}
// Tela de carregamento dos fluxos de apreciação (ELE-02): shell da página + spinner
// acessível. Substitui o `return null` das guardas de render — que deixava a tela em
// branco (impressão de erro) enquanto user/ciclo/fase resolvem. Renderizada também
// pelo gate, a transição gate → fluxo vira um loading contínuo, sem "pisca".
export function FlowLoading() {
  const [showSpinner, setShowSpinner] = useState(() => Date.now() - lastSpinnerVisibleAt < SPINNER_CONTINUITY_MS);
  const visibleRef = useRef(showSpinner);
  visibleRef.current = showSpinner;

  useEffect(() => {
    const timer = window.setTimeout(() => setShowSpinner(true), SPINNER_DELAY_MS);

    return () => {
      window.clearTimeout(timer);

      if (visibleRef.current) {
        lastSpinnerVisibleAt = Date.now();
      }
    };
  }, []);

  return (
    <div className="mx-auto container p-4 lg:p-10" aria-busy="true">
      <FlowShellHeader />

      <div className="min-h-[40vh] flex flex-col items-center justify-center gap-4" role="status" aria-live="polite">
        {showSpinner && (
          <>
            <div
              className="w-10 h-10 rounded-full border-4 border-electric-blue border-t-transparent animate-spin"
              aria-hidden="true"
            />
            <p tabIndex={0} className="text-white font-light">
              Carregando realizações...
            </p>
          </>
        )}
      </div>
    </div>
  );
}
